import type {TechnicalFormSubmission, TechnicalFormTemplate} from '../../core/forms_requests'
import type {QHSEFormTemplate} from '../../../QHSE/core/forms_requests'
import {STATIC_FORMS} from './staticFormsConfig'

export type FormIdentity = {code: string; title: string}
export type ResponseSection = {title: string; rows: [string, string][]}

export const readSubmissionPayload = (raw?: string | null): Record<string, any> | null => {
  try {
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

const savedText = (value: unknown) => typeof value === 'string' ? value.trim() : ''
const formCode = (value: unknown) => {
  const code = savedText(value)
  return /^(common|library)$/i.test(code) ? '' : code
}

export const technicalSubmissionIdentity = (
  submission: TechnicalFormSubmission,
  template?: TechnicalFormTemplate
): FormIdentity => {
  const payload = readSubmissionPayload(submission.submissionData)
  const code = formCode(payload?.formCode) || formCode(template?.code)
  const config = STATIC_FORMS.find((form) => form.code.toLowerCase() === code.toLowerCase())
  return {
    code: code || '-',
    title: savedText(payload?.formTitle) || config?.title || template?.title || 'Form Submission',
  }
}

export const librarySubmissionIdentity = (template?: QHSEFormTemplate): FormIdentity => {
  const title = template?.name?.trim() || 'Library Form Submission'
  // formType is a category (often COMMON), never a form number.
  const code = title.match(/^([a-z]{2,}\s?\d+[a-z]*)(?=\s|[-:.)]|$)/i)?.[1]
  return {code: code || '-', title}
}

export const responseText = (value: unknown): string => {
  if (value == null || value === '') return '-'
  if (typeof value === 'string' && !value.trim()) return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) return value.map(responseText).join(', ') || '-'
  if (typeof value === 'object') {
    return Object.entries(value).map(([label, answer]) => `${label}: ${responseText(answer)}`).join('\n') || '-'
  }
  return String(value)
}

export const staticSubmissionResponses = (payload: Record<string, any>): ResponseSection[] => {
  const config = STATIC_FORMS.find((form) => form.code.toLowerCase() === savedText(payload.formCode).toLowerCase())
  const data = payload.data && typeof payload.data === 'object' && !Array.isArray(payload.data) ? payload.data : {}
  const rows: [string, string][] = []
  const known = new Set<string>()
  config?.fields.forEach((field) => {
    known.add(field.name)
    if (field.type === 'checklist' && field.items) {
      const answers = data[field.name] || {}
      field.items.forEach((item) => rows.push([`${field.label} - ${item}`, responseText(answers[item])]))
      // Preserve saved checklist questions that are no longer in the current definition.
      Object.keys(answers).filter((item) => !field.items!.includes(item))
        .forEach((item) => rows.push([`${field.label} - ${item}`, responseText(answers[item])]))
    } else {
      rows.push([field.label, responseText(data[field.name])])
    }
  })
  Object.entries(data).filter(([name]) => !known.has(name)).forEach(([name, value]) => {
    rows.push([name.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' '), responseText(value)])
  })
  if (!rows.length) throw new Error('No saved form responses are available for this submission.')
  return [{title: 'Form Responses', rows}]
}
